import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Volume2, ArrowRight, Sparkles, BookOpen, Image as ImageIcon, Headphones, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, playWord } from "../lib/api";

export default function WordListDetail() {
  const { id } = useParams();
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [def, setDef] = useState(null);
  const [speakingWord, setSpeakingWord] = useState(null);
  const [imgStatus, setImgStatus] = useState(null); // {total, images_ready, audio_ready}

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/wordlists/${id}`);
        setList(data);
      } catch { toast.error("List not found"); }
    })();
  }, [id]);

  // Poll asset pre-gen progress every 4s until complete
  useEffect(() => {
    if (!id) return;
    let stopped = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/wordlists/${id}/asset-status`);
        if (stopped) return;
        setImgStatus(data);
        const done = data.images_ready >= data.total && data.audio_ready >= data.total;
        if (!done) setTimeout(tick, 4000);
      } catch (err) { console.warn("[WordListDetail] image-status poll failed:", err); }
    };
    tick();
    return () => { stopped = true; };
  }, [id]);

  const onPronounce = async (word) => {
    setSpeakingWord(word);
    // Use the cached /words/audio pipeline (playWord) instead of /tts so
    // pressing Pronounce doesn't call OpenAI for a word that already has
    // a cached recording. Saves credits + is instant on repeat clicks.
    try { await playWord(word); } catch { toast.error("Couldn't play audio"); }
    finally { setSpeakingWord(null); }
  };

  const onPickWord = async (word) => {
    setSelected(word); setDef(null);
    try {
      const { data } = await api.post("/words/definition", { word });
      setDef(data);
    } catch { setDef({ word, definition: "A word to practice.", example: `I can spell ${word}.` }); }
  };

  if (!list) return <div className="card-soft text-center py-12 text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="wordlist-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-[#4D96FF] uppercase tracking-widest">Word list</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1E1B4B]">{list.name}</h1>
          <p className="text-slate-600 mt-1 font-semibold">{list.word_count} words to spell</p>
          {imgStatus && imgStatus.total > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid="asset-status">
              <div className="flex items-center gap-1.5">
                <ImageIcon className={`w-4 h-4 ${imgStatus.images_ready < imgStatus.total ? "text-[#FBBF24] animate-pulse" : "text-[#10B981]"}`} strokeWidth={3} />
                <span className="font-bold text-slate-600">
                  {imgStatus.images_ready < imgStatus.total
                    ? `Pictures ${imgStatus.images_ready}/${imgStatus.total}`
                    : `${imgStatus.total} pictures ready`}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <Headphones className={`w-4 h-4 ${imgStatus.audio_ready < imgStatus.total ? "text-[#4D96FF] animate-pulse" : "text-[#10B981]"}`} strokeWidth={3} />
                <span className="font-bold text-slate-600">
                  {imgStatus.audio_ready < imgStatus.total
                    ? `Audio ${imgStatus.audio_ready}/${imgStatus.total}`
                    : `${imgStatus.total} sounds ready`}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {list.is_personal && (
            <Link
              to="/my-words"
              className="px-4 py-2 rounded-2xl bg-[#FBBF24] text-[#1E1B4B] font-bold shadow-[0_4px_0_#B45309] active:translate-y-[2px] active:shadow-[0_2px_0_#B45309] inline-flex items-center gap-2"
              data-testid="manage-my-words-btn"
            >
              <Pencil className="w-5 h-5" strokeWidth={3} /> Manage
            </Link>
          )}
          {list.word_count > 0 && (
            <Link to={`/practice/${list.id}`} className="btn-clay-blue" data-testid="start-practice-btn">
              <BookOpen className="w-5 h-5" strokeWidth={3} /> Practice
            </Link>
          )}
          {list.word_count > 0 && (
            <Link to={`/listen/${list.id}`} className="btn-clay-green" data-testid="start-listen-btn">
              <Headphones className="w-5 h-5" strokeWidth={3} /> Listen
            </Link>
          )}
          {list.word_count > 0 && (
            <Link to={`/test/${list.id}`} className="btn-clay" data-testid="start-test-btn">
              <ArrowRight className="w-5 h-5" strokeWidth={3} /> Test
            </Link>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        <div className="card-soft">
          <h2 className="font-display text-xl font-bold text-[#1E1B4B] mb-4">All words</h2>
          <div className="flex flex-wrap gap-2" data-testid="words-grid">
            {list.words.map((w) => (
              <button
                key={w}
                onClick={() => onPickWord(w)}
                className={`px-4 py-2 rounded-full font-bold text-base transition-all ${
                  selected === w
                    ? "bg-[#4D96FF] text-white shadow-[0_4px_0_#1D4ED8]"
                    : "bg-blue-50 text-[#1E1B4B] hover:bg-blue-100"
                }`}
                data-testid={`word-chip-${w}`}
              >
                {w}
              </button>
            ))}
          </div>
        </div>

        <motion.div
          key={selected || "empty"}
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", bounce: 0.3 }}
          className="card-soft h-fit sticky top-24"
          data-testid="word-detail-card"
        >
          {!selected ? (
            <div className="text-center py-8">
              <Sparkles className="w-10 h-10 mx-auto text-[#FBBF24]" strokeWidth={3} />
              <p className="mt-3 font-bold text-[#1E1B4B]">Tap a word to learn it</p>
              <p className="text-sm text-slate-500">Hear the pronunciation and see a friendly meaning.</p>
            </div>
          ) : (
            <div>
              <div className="text-sm font-bold text-[#4D96FF] uppercase tracking-widest">Selected word</div>
              <div className="font-display text-5xl font-bold text-[#FF6B6B] text-center my-3 tracking-wider">
                {selected}
              </div>
              <div className="text-center text-xl tracking-[0.3em] font-bold text-[#1E1B4B] mb-4">
                {selected.toUpperCase().split("").join("-")}
              </div>
              <button
                onClick={() => onPronounce(selected)}
                disabled={speakingWord === selected}
                className="btn-clay-blue w-full"
                data-testid="pronounce-selected-btn"
              >
                <Volume2 className={`w-5 h-5 ${speakingWord === selected ? "pulse-soft" : ""}`} strokeWidth={3} />
                {speakingWord === selected ? "Speaking…" : "Pronounce"}
              </button>
              <div className="mt-5 space-y-2">
                <div>
                  <div className="text-xs font-bold uppercase text-slate-500">Meaning</div>
                  <div className="text-base font-semibold text-[#1E1B4B]">{def?.definition || "Loading…"}</div>
                </div>
                <div>
                  <div className="text-xs font-bold uppercase text-slate-500">Example</div>
                  <div className="text-base italic text-slate-700">"{def?.example || "…"}"</div>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
